import { useCallback, useContext, useEffect, useMemo, useRef, useState, type DragEvent } from "react"
import { AuthContext } from "@/providers/AuthProvider"
import { Icon } from "@/components/Icon"
import type {
  OiaDocumentRef,
  OiaDocumentRole,
  OiaDocumentSummary,
  OiaResolvedOrderFormat,
  OiaTemplateSummary,
} from "../../types"
import { bucketChecklist, bucketOf } from "@/features/intake/intake-buckets"
import { DocViewerModal, type ViewableFile } from "@/features/oio/components/DocViewerModal"
import { OIA_INTAKE, type OiaIntakeBucketId } from "../../config/intake-buckets"
import { ACCEPT_ATTR, screenUploads } from "../../lib/uploads"
import type { StartDraftInput } from "../../hooks/useOiaRuns"
import type { OiaNotice } from "../../store/oia-case.store"
import { NoticeBanner, Spinner } from "../common"
import { oiaService } from "../../services/oia.service"
import { isOiaMock } from "../../mock/oiaMockMode"

/**
 * Intake: the whole appeal file in one drop — no fixed slots.
 *
 * WHY NO SLOTS: real paper-books bundle the memo, the impugned order and the
 * exhibits into one PDF, and a slot is only ever a hint. The extraction
 * service identifies and names every file (and splits composites) itself;
 * uploads are never trusted by slot, and a mis-tagged upload once drafted a
 * confident wrong order.
 *
 * WHY THREE BUCKETS AND NOT NINETEEN ROLES: each file is read as it lands and
 * sorted into what it is FOR — the order under appeal, the appeal and its
 * grounds, or anything else (config/intake-buckets.ts). The fine-grained role
 * was unstable exactly in the long tail (an affidavit read three ways in three
 * runs) and the officer was being asked to referee it per row with no way to
 * open the file. Now the row shows the title PRINTED on the page, the name
 * opens the file, the menu has three choices, and a checklist says what the
 * bundle still lacks before a run is paid for. Said, never enforced.
 *
 * One optional box, "Your view of the case": it never steers the first draft
 * (the draft is decided on the statute and the record); it is compared with
 * every decision afterwards and each disagreement becomes a next step.
 */

const LAST_TEMPLATE_KEY = "oia-last-template"

interface Row {
  key: string
  filename: string
  size: number | null
  roleHint: OiaDocumentRole
  file?: File
  uploaded?: OiaDocumentRef
  /**
   * What this file turned out to be, decided while the officer is still
   * looking at it. `pending` covers the upload AND the identify call: to the
   * officer they are one wait, and splitting them would put two spinners on
   * one row. `failed` is not an error state — the row keeps its slot and the
   * officer can name it themselves.
   */
  identifyState?: "pending" | "done" | "failed"
  detectedRole?: OiaDocumentRole
  /** The paper's own kind ("covering_letter"). Steadier than the role, and
   *  what the bucket is decided from first (config/intake-buckets.ts). */
  detectedSubType?: string
  detectedTitle?: string
  /** The officer moved this file. It outranks everything the reader said. */
  bucketOverride?: OiaIntakeBucketId
}

export interface OiaIntakeProps {
  /** The case this intake belongs to: identification is per case. */
  threadId: string
  /** Keep files local until the simulator confirms generation and creates a case. */
  stagedOnly?: boolean
  /** Files uploaded by an earlier attempt of this case (kept on "start again"). */
  existing: OiaDocumentRef[]
  /** What the extraction service made of them (after a run). */
  identified: OiaDocumentSummary[]
  templates: OiaTemplateSummary[] | null
  templatesFailed: boolean
  initialTemplateId: string
  initialCaseView: string
  busy: boolean
  notice: OiaNotice | null
  /** Why a previous attempt stopped (intake_blocked / failed), shown on top. */
  blockedMessage?: string | null
  onGenerate: (input: StartDraftInput) => Promise<boolean>
}

function readLastTemplate(): string {
  try {
    return localStorage.getItem(LAST_TEMPLATE_KEY) ?? ""
  } catch {
    return ""
  }
}

function rememberTemplate(id: string) {
  try {
    if (id) localStorage.setItem(LAST_TEMPLATE_KEY, id)
  } catch {
    /* per-viewer convenience only */
  }
}

function fileSize(bytes: number | null): string {
  if (bytes === null) return "uploaded"
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function OiaIntake({
  threadId,
  stagedOnly = false,
  existing,
  identified,
  templates,
  templatesFailed,
  initialTemplateId,
  initialCaseView,
  busy,
  notice,
  blockedMessage,
  onGenerate,
}: OiaIntakeProps) {
  const auth = useContext(AuthContext)
  const userId = auth?.session?.user?.id ?? "anonymous"
  const [rows, setRows] = useState<Row[]>(() =>
    existing.map((ref, i) => ({
      key: `existing-${i}-${ref.url}`,
      filename: ref.filename || ref.url.split("/").pop() || "document",
      size: null,
      roleHint: ref.role_hint,
      uploaded: ref,
    })),
  )
  const [caseView, setCaseView] = useState(initialCaseView)
  const [templateId, setTemplateId] = useState(() => initialTemplateId || readLastTemplate())
  /**
   * The format READ OFF THE PAPERS, which is the one the draft uses.
   *
   * `templateId` above is no longer a question asked at intake; it is only
   * the fallback for papers whose letterhead could not be read — the format
   * this officer last used. An appellate authority sits in one office under
   * one Act, so asking them to pick a format is asking them to restate their
   * own posting.
   */
  const [detectedFormat, setDetectedFormat] = useState<OiaResolvedOrderFormat | null>(null)
  /** The officer overriding what the papers said. Rare, and never demanded. */
  const [formatOverridden, setFormatOverridden] = useState(false)
  const [rejected, setRejected] = useState<string[]>([])
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Default to the first template once the list arrives, unless a remembered
  // or previous choice is still offered.
  useEffect(() => {
    if (!templates || templates.length === 0) return
    if (!templates.some((t) => t.template_id === templateId)) setTemplateId(templates[0].template_id)
  }, [templates, templateId])

  const identifiedByName = useMemo(() => {
    const map = new Map<string, OiaDocumentSummary>()
    for (const doc of identified) if (!doc.split_from && !map.has(doc.filename)) map.set(doc.filename, doc)
    return map
  }, [identified])

  /**
   * Upload ONE file and find out what it is, without blocking anything else.
   *
   * Each file races on its own: the officer drops five documents and five
   * rows settle independently, because a batch would make the slowest file
   * hold up the other four and the whole point is that a row resolves while
   * they are still looking at it.
   *
   * The upload moves here from Generate deliberately. Identification needs a
   * stored file, so a file the officer has not uploaded cannot be named — and
   * doing it now also means Generate no longer waits on the network for
   * documents chosen minutes earlier.
   *
   * Nothing here can stop the officer: a failed upload or a failed identify
   * leaves the row on its own slot, still generatable, still nameable by
   * hand. The one thing it must not do is silently drop the file.
   */
  const identifyRow = useCallback(
    async (key: string, file: File) => {
      const patch = (fields: Partial<Row>) =>
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...fields } : r)))
      patch({ identifyState: "pending" })
      try {
        const url = await oiaService.uploadFile(file, userId)
        const ref: OiaDocumentRef = { url, role_hint: "other", filename: file.name }
        patch({ uploaded: ref })
        const answer = await oiaService.identify(threadId, { documents: [ref] })
        const found = answer.documents?.[0]
        const role = found?.detected_role
        // The first file whose letterhead can be read settles the format.
        // Later files do not re-decide it: every paper of one appeal comes
        // from one regime, and a second answer could only be noise or a
        // stray exhibit from another proceeding.
        const format = answer.order_format
        if (format?.template_id) {
          setDetectedFormat((prev) => prev ?? format)
        }
        patch({
          identifyState: role ? "done" : "failed",
          detectedRole: role ?? undefined,
          detectedSubType: found?.sub_type || undefined,
          detectedTitle: found?.canonical_name || undefined,
          // The classifier decides; the slot follows it so what the officer
          // sees and what the run receives cannot disagree.
          roleHint: role ?? "other",
        })
      } catch {
        // Keep the file. An unnamed document is still the officer's evidence.
        patch({ identifyState: "failed" })
      }
    },
    [threadId, userId],
  )

  /**
   * Which of the three things this file is FOR. The officer's own move wins;
   * then what was just read off THIS upload; then what an earlier run made of
   * a file of the same name; then a role they chose before buckets existed.
   */
  const bucketOfRow = useCallback(
    (row: Row): OiaIntakeBucketId => {
      if (row.bucketOverride) return row.bucketOverride
      const earlier = row.identifyState ? undefined : identifiedByName.get(row.filename)
      const role = row.detectedRole ?? earlier?.detected_role ?? (row.roleHint !== "other" ? row.roleHint : undefined)
      return bucketOf(OIA_INTAKE, role, row.detectedSubType ?? earlier?.sub_type)
    },
    [identifiedByName],
  )
  const settled = rows.every((r) => r.identifyState !== "pending")
  const checklist = useMemo(
    () => bucketChecklist(OIA_INTAKE.buckets, rows.map((r) => ({ filename: r.filename, bucket: bucketOfRow(r) })), settled),
    [rows, bucketOfRow, settled],
  )
  const missing = checklist.filter((c) => c.missing)
  const [viewing, setViewing] = useState<ViewableFile | null>(null)

  /**
   * A RE-DRAFT MUST BE AS GOOD AS A FRESH DROP. Files kept from an earlier
   * attempt are already stored, so nothing re-identifies them — and the
   * order's format and OFFICE are only ever learned from an identify answer.
   * "Draft again" therefore sent no office, and reproduced the very order it
   * was meant to repair: "[name of the office to be entered]" over a
   * letterhead the backend has on file. So when the intake opens with kept
   * files and nothing resolved, the one file most likely to carry the
   * department's letterhead is read once, for the format alone: the row is
   * not re-labelled, and a failure changes nothing.
   */
  const formatProbed = useRef(false)
  useEffect(() => {
    if (stagedOnly || formatProbed.current || detectedFormat) return
    const kept = rows.filter((r) => r.uploaded && !r.file)
    if (kept.length === 0) return
    const order = ["order", "appeal", "other"] as const
    const probe = [...kept].sort((a, b) => order.indexOf(bucketOfRow(a)) - order.indexOf(bucketOfRow(b)))[0]
    if (!probe?.uploaded) return
    formatProbed.current = true
    // The dev mock (?mock=oia) promises NO backend calls; it has no office to
    // resolve either, so there is nothing to probe for.
    if (isOiaMock()) return
    const ref = probe.uploaded
    void (async () => {
      try {
        const answer = await oiaService.identify(threadId, { documents: [ref] })
        if (answer?.order_format?.template_id) setDetectedFormat((prev) => prev ?? answer.order_format)
      } catch {
        /* the fallback format stands; the draft is never held up for this */
      }
    })()
  }, [rows, detectedFormat, bucketOfRow, threadId, stagedOnly])

  const addFiles = (list: FileList | File[]) => {
    const { accepted, rejected: bad } = screenUploads(Array.from(list))
    const next = accepted.map((file): Row => ({
      key: `${file.name}-${file.size}-${file.lastModified}`,
      filename: file.name,
      size: file.size,
      roleHint: "other",
      file,
    }))
    setRejected(bad)

    const existingKeys = new Set(rows.map((r) => r.key))
    const fresh = next.filter((r) => !existingKeys.has(r.key))
    if (fresh.length === 0) return

    setRows((prev) => {
      const seen = new Set(prev.map((r) => r.key))
      const trulyFresh = fresh.filter((r) => !seen.has(r.key))
      return [...prev, ...trulyFresh]
    })

    // Process fresh uploads with bounded concurrency (2 workers)
    // to prevent bursting Lambda concurrent execution limits.
    if (stagedOnly) return
    void (async () => {
      const queue = fresh.filter((r) => !!r.file)
      const concurrency = 2
      let idx = 0
      const worker = async () => {
        while (idx < queue.length) {
          const item = queue[idx++]
          if (item?.file) {
            await identifyRow(item.key, item.file).catch(() => {})
          }
        }
      }
      await Promise.all(
        Array.from({ length: Math.min(concurrency, queue.length) }, () => worker()),
      )
    })()
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files)
  }

  /**
   * What the papers said, unless the officer has overridden it, and their
   * last-used format when the papers said nothing. Never empty if any format
   * is known: an intake that cannot answer this still generates.
   */
  const effectiveTemplateId =
    (!formatOverridden && detectedFormat?.template_id) || templateId

  const generate = () => {
    rememberTemplate(effectiveTemplateId)
    // A row is uploaded OR staged, never both. Identification uploads the
    // file as it lands, so most rows arrive here already stored; sending one
    // in both lists would upload it twice and hand the run the same document
    // under two urls. `uploads` is now only the residue: a file whose upload
    // failed, which Generate retries.
    void onGenerate({
      uploads: rows
        .filter((r) => r.file && !r.uploaded)
        .map((r) => ({ file: r.file as File, roleHint: r.roleHint })),
      existing: rows
        .filter((r) => r.uploaded)
        .map((r) => ({ ...(r.uploaded as OiaDocumentRef), role_hint: r.roleHint })),
      caseView,
      templateId: effectiveTemplateId,
      // The office travels with the format it was read with — and ONLY with
      // it: an officer who overrides the format has said the letterhead
      // reading was wrong, so its office must not ride along.
      ...(!formatOverridden && detectedFormat?.office_id
        ? { officeId: detectedFormat.office_id, officerId: detectedFormat.officer_id || undefined }
        : {}),
    })
  }

  return (
    <div className="uploadpage" data-testid="oia-intake">
      <div className="uploadpage__inner" style={{ maxWidth: 760 }}>
        <div className="uploadpage__card">
          <div className="uphead">
            <h3>Order-in-Appeal</h3>
            <p>
              Drop the whole appeal file. Each document is read and sorted for you.
            </p>
          </div>

          {blockedMessage && <NoticeBanner notice={{ tone: "warn", text: blockedMessage }} />}
          {notice && <div style={{ marginTop: 10 }}><NoticeBanner notice={notice} /></div>}

          <div
            className={"dropzone" + (dragOver ? " oia-dropzone--over" : "")}
            role="button"
            tabIndex={0}
            aria-label="Add the appeal files"
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") inputRef.current?.click()
            }}
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            style={{ marginTop: 14 }}
          >
            <Icon name="upload" size={20} />
            <span>Drop all the files here, or click to choose (PDF or Word, up to 24 MB each)</span>
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            accept={ACCEPT_ATTR}
            data-testid="oia-file-input"
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files)
              e.target.value = ""
            }}
          />
          {rejected.length > 0 && (
            <div className="oia-tone--upheld" style={{ fontSize: 12, marginTop: 8 }}>
              {rejected.map((r) => (
                <div key={r}>{r}</div>
              ))}
            </div>
          )}

          {rows.length > 0 && (
            <>
              {/*
                WHAT THE BUNDLE HAS, before a run is paid for. Three lines, two
                of them required. A required line is called missing only once
                every file has been read — "you forgot the order" flashing up
                two seconds before the order is named is worse than silence.
              */}
              <div className="oia-checklist" aria-label="What this appeal file contains">
                {checklist.map(({ bucket, filenames, missing: isMissing }) => (
                  <div
                    key={bucket.id}
                    className={
                      "oia-checklist__item" +
                      (filenames.length > 0 ? " is-present" : "") +
                      (isMissing ? " is-missing" : "")
                    }
                    data-testid={`oia-bucket-${bucket.id}`}
                  >
                    <span className="oia-checklist__mark" aria-hidden="true">
                      {filenames.length > 0 ? (
                        <Icon name="check" size={13} />
                      ) : !settled ? (
                        <Spinner size={11} />
                      ) : isMissing ? (
                        <Icon name="alert" size={13} />
                      ) : null}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div className="oia-checklist__label">
                        {bucket.label}
                        {!bucket.required && <span className="oia-checklist__opt"> · optional</span>}
                      </div>
                      <div className="oia-checklist__files">
                        {filenames.length > 0 ? filenames.join(", ") : !settled ? "Reading your files…" : bucket.hint}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="oia-files" aria-label="Files in this appeal">
                {rows.map((row) => {
                  const earlier = row.identifyState ? undefined : identifiedByName.get(row.filename)
                  const title = row.detectedTitle || earlier?.canonical_name
                  const bucket = bucketOfRow(row)
                  return (
                    <div key={row.key} className="oia-file" data-testid="oia-file-row">
                      <Icon name="file" size={16} />
                      <div style={{ minWidth: 0 }}>
                        {/* The name opens the file. Without it the officer is
                            asked to confirm what a paper is without being able
                            to look at it. */}
                        {row.uploaded?.url ? (
                          <button
                            type="button"
                            className="oia-file__name oia-file__name--link"
                            title={`Open ${row.filename}`}
                            onClick={() =>
                              setViewing({
                                name: row.filename,
                                url: row.uploaded?.url,
                                label: OIA_INTAKE.buckets.find((b) => b.id === bucket)?.label ?? "",
                              })
                            }
                          >
                            {row.filename}
                          </button>
                        ) : (
                          <div className="oia-file__name" title={row.filename}>
                            {row.filename}
                          </div>
                        )}
                        <div className="oia-file__sub">{fileSize(row.size)}</div>
                        {row.identifyState === "pending" && (
                          <div className="oia-file__detected oia-file__detected--pending">
                            <Spinner size={11} /> Reading the first page…
                          </div>
                        )}
                        {row.identifyState === "failed" && (
                          <div className="oia-file__detected oia-file__detected--unknown">
                            Could not read this one — say what it is
                          </div>
                        )}
                        {/* THE PRINTED TITLE, not the classifier's label. The
                            title is what is on the page and was the same in
                            every run; the fine-grained role was not. */}
                        {row.identifyState !== "pending" && title && (
                          <div className="oia-file__detected">
                            {row.detectedTitle ? "Reads" : "Named"} “{title}”
                          </div>
                        )}
                      </div>
                      <select
                        className="oia-select"
                        value={bucket}
                        aria-label={`What is ${row.filename}?`}
                        onChange={(e) => {
                          const moved = e.target.value as OiaIntakeBucketId
                          setRows((prev) =>
                            prev.map((r) =>
                              r.key === row.key
                                ? { ...r, bucketOverride: moved, roleHint: OIA_INTAKE.hintFor[moved] }
                                : r,
                            ),
                          )
                        }}
                        disabled={busy}
                      >
                        {OIA_INTAKE.buckets.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className="btn btn--quiet btn--sm"
                        aria-label={`Remove ${row.filename}`}
                        onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                        disabled={busy}
                      >
                        <Icon name="x" size={14} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {/*
            THE FORMAT IS NOT ASKED FOR. It is read off the letterhead of the
            papers the officer just uploaded, and shown here as a conclusion
            with the file it came from, so a wrong answer is traceable in one
            glance. The select appears only when they choose to override it,
            or when nothing could be read — never as a step to complete.
          */}
          {(detectedFormat || rows.length > 0 || templatesFailed) && (
            <div className="oia-field">
              {/* The label points at the select WHENEVER one is rendered; a
                  control the officer can operate but cannot reach by its
                  label is not reachable at all. */}
              <label
                className="oia-label"
                htmlFor={detectedFormat && !formatOverridden ? undefined : "oia-template"}
              >
                Order format
              </label>
              {detectedFormat && !formatOverridden ? (
                <div className="oia-resolved">
                  <div className="oia-resolved__value">
                    <Icon name="check" size={14} />
                    <span>{detectedFormat.title || detectedFormat.template_id}</span>
                  </div>
                  <div className="oia-resolved__from">
                    {detectedFormat.issuing_authority
                      ? `From ${detectedFormat.resolved_from} — “${detectedFormat.issuing_authority}”`
                      : `From ${detectedFormat.resolved_from}`}
                    {templates && templates.length > 1 && (
                      <button
                        type="button"
                        className="linklike"
                        onClick={() => {
                          setTemplateId(detectedFormat.template_id)
                          setFormatOverridden(true)
                        }}
                        disabled={busy}
                      >
                        Change
                      </button>
                    )}
                  </div>
                </div>
              ) : templates && templates.length > 0 ? (
                <select
                  id="oia-template"
                  className="oia-select"
                  value={templateId}
                  onChange={(e) => {
                    setTemplateId(e.target.value)
                    setFormatOverridden(true)
                  }}
                >
                  {templates.map((t) => (
                    <option key={t.template_id} value={t.template_id}>
                      {t.title || t.template_id}
                      {t.description ? ` — ${t.description}` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="oia-field__hint">
                  {templatesFailed
                    ? "The office formats could not be loaded; the draft will use your office's default format."
                    : templates
                      ? "No formats are set up; the draft will use the default format."
                      : "Reading the format from your documents…"}
                </div>
              )}
            </div>
          )}

          <div className="oia-field">
            <label className="oia-label" htmlFor="oia-case-view">
              Your view of the case (optional)
            </label>
            <textarea
              id="oia-case-view"
              className="oia-textarea"
              value={caseView}
              onChange={(e) => setCaseView(e.target.value)}
              placeholder="e.g. The demand on the second head is unsustainable because the supplier was genuine; the rest stands."
            />
            <div className="oia-field__hint">
              Optional. The draft is decided on the record; where it differs from your view, you get a next step.
            </div>
          </div>

          {/*
            SAID, NOT ENFORCED. The reader can be wrong, and an intake that
            refuses a complete bundle because it misread one file is the worse
            failure. The officer is told plainly what looks absent and what it
            will cost, and Generate stays theirs.
          */}
          {missing.length > 0 && (
            <div className="oia-missing" role="status" data-testid="oia-missing">
              <Icon name="alert" size={15} />
              <div>
                <strong>
                  {missing.map((m) => m.bucket.label).join(" and ")}{" "}
                  {missing.length === 1 ? "was" : "were"} not found among these files.
                </strong>{" "}
                A draft cannot be written without {missing.length === 1 ? "it" : "them"}, and the run would stop after
                reading everything. Add the file — or, if it is here under another name, move it with the menu beside
                it.
              </div>
            </div>
          )}

          <div className="gen-bar" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span className="oia-muted" style={{ fontSize: 12, flex: 1 }}>
              {rows.length === 0
                ? "Add at least one file."
                : `${rows.length} file${rows.length === 1 ? "" : "s"}. A full draft usually takes several minutes.`}
            </span>
            <button className="btn btn--primary" disabled={rows.length === 0 || busy} onClick={generate}>
              {busy ? <Spinner /> : <Icon name="sparkle" size={15} />} Generate the draft
            </button>
          </div>
        </div>
      </div>
      {viewing && <DocViewerModal file={viewing} onClose={() => setViewing(null)} />}
    </div>
  )
}
